import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Modal } from '@core/enums/modal';
import { Permission } from '@core/enums/permissions';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { StudentRequestReason } from '@core/enums/student-request-reason';
import { EnrollmentTypeIdEnum } from '@core/enums/enrollment-type-id.enum';
import { GlobalRatingPeriod } from '@core/enums/global-rating-period-id.enum';
import { IUserClassSearchRequestDto } from '@core/interfaces/iclasses';
import { IRatingPeriod, ISchoolYear } from '@core/interfaces/iratingperiod';
import { IStudentSearchResponseDto } from '@core/interfaces/istudent';
import {
  IStudentRequest,
  IStudentRequestBatch,
} from '@core/interfaces/istudent-request';
import { AgencyService } from '@core/services/agency.service';
import { LookupService } from '@core/services/lookup.service';
import { PermissionService } from '@core/services/permission.service';
import { StateService } from '@core/services/state.service';
import { StudentRequestService } from '@core/services/student-request.service';
import { ToastService } from '@core/services/toast.service';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { take } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-transfer-child',
    templateUrl: './transfer-child.component.html',
    styleUrls: ['./transfer-child.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransferChildComponent implements OnInit {
  @Output() valueChange = new EventEmitter();
  isAdmin = false;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  isProcessing = false;
  child: any;
  payload: IUserClassSearchRequestDto = {};
  initialRatingPeriodId: any | undefined;
  transferChildForm: FormGroup | any;
  submitBtnText = '';
  isBatchTransfer = false;
  students?: IStudentSearchResponseDto[];
  agencyId?: number;
  label = '';
  ratingStartDate: Date = new Date();
  ratingEndDate: Date = new Date();
  yesterday! : number;
  agencySoftImplementationEnabled = false;
  siteSoftImplementationEnabled = false;
  classroomSoftImplementationEnabled = false;
  isSoftImplementation = false;
  stateId = 0;
  targetSchoolYearId = 0;
  enforceStateConfig: boolean = false;
  show2025 = false;
  ratingViewVersion = RatingViewVersionEnum.VERSION_2015;
  selectedRatingPeriod?: IRatingPeriod;
  isUserState2025Enforced = false;

  get site() {
    return this.transferChildForm.get('site');
  }
  get classroom() {
    return this.transferChildForm.get('classroom');
  }
  get ratingPeriod() {
    return this.transferChildForm.get('ratingPeriod');
  }
  get comment() {
    return this.transferChildForm.get('Comment');
  }
  get classEnrollmentDate() {
    return this.transferChildForm.get('classEnrollmentDate');
  }
  get ageGradeId() {
    return this.transferChildForm.get('ageGradeId');
  }

  constructor(
    public toastService: ToastService,
    public permissionService: PermissionService,
    private fb: FormBuilder,
    private studentRequestService: StudentRequestService,
    private agencyService: AgencyService,
    private lookupService: LookupService,
    public modal: MatDialog,
    public dialogRef: MatDialogRef<TransferChildComponent>,
    private stateService: StateService,
    private translocoService: TranslocoService,
    @Inject(MAT_DIALOG_DATA) public editData: any
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.calculateYesterdayDate();
    this.initializeForms();
    this.stateService.isUserState2025Enforced().pipe(take(1)).subscribe((enforced) => (this.isUserState2025Enforced = enforced));
    this.isAdmin = this.permissionService.checkPermission(
      Permission.AdminEnrollmentByPass
    );
    this.label = this.translocoService.translate('user.manageEnrollment.transferChild.title');
    this.submitBtnText = this.isAdmin
      ? this.translocoService.translate('user.manageEnrollment.transferChild.submit')
      : this.translocoService.translate('user.manageEnrollment.transferChild.submitForApproval');

    if (Array.isArray(this.editData.studentBatch)) {
      const { studentBatch, agencyId } = this.editData;
      this.students = studentBatch;
      this.isBatchTransfer = true;
      this.agencyId = agencyId;
      this.label = this.translocoService.translate('user.manageEnrollment.transferChild.batchTitle');
      this.agencyService.getAgency(agencyId)
        .pipe(take(1))
        .subscribe((agency) => {
          this.agencySoftImplementationEnabled = agency?.isSoftImplementation ?? false;
          this.stateId = agency?.stateId ?? this.stateId;
          this.updateSoftImplementation();
          this.checkStateConfiguration();
        });
    } else {
      this.child = this.editData.data;
      this.agencyId = this.child.agencyId;
      this.loadAgencyStateConfiguration();
    }
  }

  initializeForms(): void {
    this.transferChildForm = this.fb.group({
      site: [null, Validators.required],
      classroom: [null, Validators.required],
      ratingPeriod: [null, Validators.required],
      Comment: ['', [Validators.maxLength(150)]],
      classEnrollmentDate: [null, [Validators.required]],
      ageGradeId: [null],
    });

    this.agencySoftImplementationEnabled = this.editData.data?.isAgencySoftImplementation;
    this.updateSoftImplementation();
  }

  loadAgencyStateConfiguration(): void {
    if (!this.agencyId) return;
    this.agencyService.getAgency(this.agencyId)
      .pipe(take(1))
      .subscribe((agency) => {
        this.stateId = agency?.stateId ?? this.stateId;
        this.checkStateConfiguration();
      });
  }

  checkStateConfiguration(): void {
    if (this.stateId) {
      this.stateService.getStatesById(this.stateId)
        .pipe(take(1))
        .subscribe(state => {
          this.ratingViewVersion = state.ratingViewVersionId || RatingViewVersionEnum.VERSION_2015;
          this.recomputeShow2025();
        });
    }
  }

  recomputeShow2025(): void {
    this.show2025 = this.enforceStateConfig && this.ratingViewVersion === RatingViewVersionEnum.VERSION_2025;
    this.updateFormValidation();
  }

  updateSoftImplementation(): void {
    this.isSoftImplementation = this.classroomSoftImplementationEnabled ||
                               this.siteSoftImplementationEnabled ||
                               this.agencySoftImplementationEnabled;
    
    if (this.stateId > 0) {
      this.checkStateConfiguration();
    }
  }

  updateFormValidation(): void {
    if (this.isSoftImplementation || this.show2025) {
      this.ageGradeId?.setValidators([Validators.required]);
    } else {
      this.ageGradeId?.clearValidators();
    }

    this.ageGradeId?.updateValueAndValidity();
  }

  calculateYesterdayDate(): void {
    const currentDate = new Date();
    let yesterday = new Date(currentDate);
    yesterday.setDate(currentDate.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);

    this.yesterday =  yesterday.getTime();
  }

  handleSite(site: any): void {
    this.site.setValue(site?.id);
    this.payload.siteId = site?.id;
    this.classroom.setValue(null);
    this.siteSoftImplementationEnabled = site?.item.isSoftImplementation;
    this.stateId = site?.item.stateId || 0;
    this.updateSoftImplementation();
  }

  handleClassroom(classroom: any): void {
    this.classroom.setValue(classroom?.id);
    this.classroomSoftImplementationEnabled = classroom?.item?.isSoftImplementation;
    this.updateSoftImplementation();
  }

  handleRatingPeriod(ratingPeriod: IRatingPeriod): void {
    this.enforceStateConfig = ratingPeriod?.schoolYear?.enforceStateConfig ?? false;
    this.selectedRatingPeriod = ratingPeriod;
    this.targetSchoolYearId = ratingPeriod.schoolYearId;
    this.ratingPeriod.setValue(ratingPeriod?.id);
    this.payload.ratingPeriodId = ratingPeriod?.id;
    this.classroom.setValue(null);
    this.ratingStartDate = new Date(ratingPeriod?.minDate);
    this.ratingEndDate = new Date(ratingPeriod?.maxDate);
    this.handleDate(this.classEnrollmentDate.value);
    this.updateSoftImplementation();
    this.recomputeShow2025();
  }

  handleAgeGrade(ageGrade: any): void {
    this.ageGradeId.setValue(ageGrade?.id);
  }

  onSubmit() {
    if (this.isProcessing) return;
    
    this.handleDate(this.classEnrollmentDate.value);
    if (this.transferChildForm.invalid) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.common.requiredFieldsError')
      );
      return;
    }

    const requires2025AgeGrade = this.isSoftImplementation || this.show2025;

    if (requires2025AgeGrade && !this.ageGradeId.value) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.common.requiredFieldsError')
      );
      return;
    }

    if (this.isUserState2025Enforced && !this.show2025) {
      this.toastService.error(this.translocoService.translate('shared.ratingPeriods.ratingViewRejected'));
      return;
    }

    this.isProcessing = true;
    const successMessage = this.isAdmin
      ? this.translocoService.translate('user.manageEnrollment.transferChild.transferSuccess')
      : this.translocoService.translate('user.manageEnrollment.transferChild.transferRequestSent');

    const transferErrorMessage = this.translocoService.translate(
      'user.manageEnrollment.transferChild.ratingPeriodOrderError'
    );
    if (this.isBatchTransfer) {
      const payload: IStudentRequestBatch = {
        studentRequestDtos: [],
        siteId: this.site.value,
        requestReasonId: StudentRequestReason.Transfer,
      };

      if (this.students?.length) {
        if (this.students.some(student => this.ratingPeriod.value < student.ratingPeriodId )) {
          this.toastService.error(transferErrorMessage);
          this.isProcessing = false;
          return;
        }

        const isTransferringToSameClass = this.students?.some(
          (student: IStudentSearchResponseDto) =>
            student.classId == this.classroom.value
        );

        if (isTransferringToSameClass) {
          this.showModal(
            this.translocoService.translate('user.manageEnrollment.transferChild.batchSameClassroomError')
          );
          return;
        }

        const enrollmentTypes = [...new Set(this.students?.map(student => student.enrollment))];

        if (enrollmentTypes.length > 1) {
          this.showModal(
            this.translocoService.translate('user.manageEnrollment.transferChild.mixedEnrollmentTypesError')
          );
          this.isProcessing = false;
          return;
        }

        const hasFieldStudyStudent = this.students?.some(student => student.enrollmentTypeId === EnrollmentTypeIdEnum.FieldStudy);
        const isTargetFall2025 = this.selectedRatingPeriod?.globalRatingPeriod?.id === GlobalRatingPeriod.FALL_2025;
        if (hasFieldStudyStudent && !isTargetFall2025) {
          this.toastService.error(
            this.translocoService.translate('user.manageEnrollment.transferChild.fieldStudyRatingPeriodError')
          );
          this.isProcessing = false;
          return;
        }

        const studentRequest: IStudentRequest[] = this.students.map(
          (student: IStudentSearchResponseDto) => ({
            fromClassId: student.classId,
            toClassId: this.classroom.value,
            studentId: student.id,
            requestReasonId: StudentRequestReason.Transfer,
            comment: this.comment.value,
            siteId: this.site.value,
            studentName: `${student.firstName} ${student.lastName}`,
            classEnrollmentDate: this.classEnrollmentDate.value,
            classExitDate: new Date(this.getStudentExitDate(student)),
            ageGradeId: this.ageGradeId.value,
            studentClassEnrollmentId: student.sceId,
          })
        );
        payload.studentRequestDtos = studentRequest;
      }

      if (payload.studentRequestDtos) {
        this.studentRequestService.checkDuplicateBatch(payload)
        .pipe(take(1))
        .subscribe((duplicates) => {
          if(duplicates && duplicates.length > 0) {
            this.dialogRef.close({ success: false, duplicates: duplicates });
          }
          else {
            this.studentRequestService
            .processBatchRequest(payload)
            .pipe(take(1))
            .subscribe((_) => {
              this.isProcessing = false;
              this.dialogRef.close({ success: true });
            });
          }
        });
      }
    } else {
      if (this.child.classId == this.classroom.value) {
        this.showModal(
          this.translocoService.translate('user.manageEnrollment.transferChild.sameClassroomError')
        );
        this.isProcessing = false;
        return;
      }

      if (this.ratingPeriod.value < this.child.ratingPeriodId) {
        this.toastService.error(transferErrorMessage);
        this.isProcessing = false;
        return;
      }

      const isTargetFall2025 = this.selectedRatingPeriod?.globalRatingPeriod?.id === GlobalRatingPeriod.FALL_2025;
      if (this.child.enrollmentTypeId === EnrollmentTypeIdEnum.FieldStudy && !isTargetFall2025) {
        this.toastService.error(
          this.translocoService.translate('user.manageEnrollment.transferChild.fieldStudyRatingPeriodError')
        );
        this.isProcessing = false;
        return;
      }

      let payload: IStudentRequest = {
        fromClassId: this.child.classId,
        toClassId: this.classroom.value,
        studentId: this.child.id,
        studentClassEnrollmentId: this.child.studentClassEnrollmentId,
        requestReasonId: StudentRequestReason.Transfer,
        comment: this.comment.value,
        siteId: this.site.value,
        classEnrollmentDate: this.classEnrollmentDate.value,
        classExitDate: new Date(this.getStudentExitDate(this.child)),
        ageGradeId: this.ageGradeId.value,
      };

      this.studentRequestService
        .addStudentRequest(payload, successMessage)
        .pipe(take(1))
        .subscribe(() => {
          this.isProcessing = false;
          this.dialogRef.close({ success: true });
        });
    }
  }


  showModal(displayMessage: string) {
    let event = { data: { modalInfo: {} } };
    event.data.modalInfo = {
      title: '',
      message: displayMessage,
      name: '',
      primaryBtnClass: 'blue',
      primaryBtnText: Modal.OK,
    };
    this.modal.open(ConfirmationModalComponent, {
      data: event ? event : undefined,
    });
  }

  handleDate(event: any): void {
    this.classEnrollmentDate.setValue(event);

    const enrollmentDate = new Date(event);
    enrollmentDate.setHours(0, 0, 0, 0);
    const classEnrollmentDate = enrollmentDate.getTime();

    if (this.classEnrollmentDate.value && this.ratingPeriod.value) {
      if (classEnrollmentDate < this.ratingStartDate.getTime() ||
        classEnrollmentDate > this.ratingEndDate.getTime()) {
        this.transferChildForm.get('classEnrollmentDate').setErrors({dateRange: true});
      } else if (this.checkExitDate(classEnrollmentDate)) {
        this.transferChildForm.get('classEnrollmentDate').setErrors({exitDate: true});
      } else {
        this.classEnrollmentDate.setErrors(null);
      }
    }
  }

  checkExitDate(classEnrollmentDate: number): boolean {
    if (this.isBatchTransfer && this.students) {
      return this.students.some(student => student.ratingPeriodId != this.ratingPeriod.value &&
        this.invalidEnrollmentDate(student, classEnrollmentDate));
    } else if (this.child && (this.child.ratingPeriodId != this.ratingPeriod.value)) {
      return this.invalidEnrollmentDate(this.child, classEnrollmentDate);
    }

    return false;
  }

  invalidEnrollmentDate(student: IStudentSearchResponseDto, classEnrollmentDate: number): boolean {
    const exitDate = this.getStudentExitDate(student);
    return exitDate >= classEnrollmentDate;
  }

  getStudentExitDate(student: IStudentSearchResponseDto) {
    const currentRatingPeriodEndDate = new Date(student.ratingPeriodEndDate).getTime();
    const exitDate = currentRatingPeriodEndDate < this.yesterday ? currentRatingPeriodEndDate : this.yesterday;
    return exitDate;
  }
}
